<template>
  <div class="pdf-wrapper">
    <div class="row mx-0">
      <div class="col-12 pb-0">
        <div class="title">{{ responseData.title_page.title }}</div>
        <div class="subtitle">Environment</div>
      </div>
      <div class="top-logo">
        <img
          class="profile-img img-fluid mt-2"
          src="/assets/images/logos/arva-logo.png"
          alt="User"
        />
      </div>
    </div>
    <div class="row background-dim mx-0">
      <div class="col" />
    </div>
    <div class="row mx-0">
      <div class="col-6 py-0">
        <report-info-table :dataTableObject="descriptionTable" />
      </div>
      <div class="col-6 py-0 px-0">
        <div class="map-wrapper">
          <div id="mapbox-map" class="fill-wrapper"></div>
        </div>
        <div class="control-wrapper">
          <div class="d-flex">
            <div class="control-dot"></div>
            <div class="dot-text lg-font">Control</div>
          </div>
          <div class="d-flex">
            <div class="trial-dot"></div>
            <div class="dot-text lg-font">Trial</div>
          </div>
          <div class="d-flex">
            <div class="field-dot"></div>
            <div class="dot-text lg-font">Field</div>
          </div>
        </div>
        <div class="map-footer">
          <div class="row scale-text mx-0">
            <div class="col red-fill text-center py-1">
              {{ colorValueRanges[0] | prettyInteger }}-{{
                colorValueRanges[1] | prettyInteger
              }}
            </div>
            <div class="col orange-fill text-center py-1">
              {{ colorValueRanges[1] | prettyInteger }}-{{
                colorValueRanges[2] | prettyInteger
              }}
            </div>
            <div class="col yellow-fill text-center py-1">
              {{ colorValueRanges[2] | prettyInteger }}-{{
                colorValueRanges[3] | prettyInteger
              }}
            </div>
            <div class="col light-green-fill text-center py-1">
              {{ colorValueRanges[3] | prettyInteger }}-{{
                colorValueRanges[5] | prettyInteger
              }}
            </div>
            <div class="col bright-green-fill text-center py-1">
              {{ colorValueRanges[5] | prettyInteger }}-{{
                colorValueRanges[6] | prettyInteger
              }}
            </div>
            <div class="col green-fill text-center py-1">
              {{ colorValueRanges[6] | prettyInteger }}-{{
                colorValueRanges[8] | prettyInteger
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row background-dim mx-0">
      <div class="col" />
    </div>
    <div class="row mx-0">
      <div class="col-6 py-0">
        <apexchart
          v-if="responseData"
          type="boxPlot"
          height="350"
          :options="envOptions"
          :series="envSeries"
        />
      </div>
      <div class="col-6 py-0">
        <apexchart
          v-if="responseData"
          type="radar"
          height="350"
          :options="radarOptions"
          :series="radarSeries"
        />
      </div>
    </div>
    <div class="row background-dim mx-0">
      <div class="col" />
    </div>
    <div class="row mx-0">
      <div class="col-12 pt-0">
        <apexchart
          v-if="responseData"
          type="boxPlot"
          height="350"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
    </div>
    <div class="footer d-flex align-items-center justify-content-between">
      <div class="ml-2">
        Confidential
      </div>
      <div class="mr-3">
        <i class="fa fa-copyright" /> 2021 Arva Intelligence
        <i class="fa fa-trademark" />
      </div>
    </div>
  </div>
</template>
<script>
import _ from "lodash"
import { GeoJsonLayer } from "@deck.gl/layers"
import { MapboxLayer } from "@deck.gl/mapbox"
import mapboxgl from "mapbox-gl"

import { MAPBOX_TOKEN } from "@/constants/map"
import { HARVEST_YIELD_COLORS } from "@/components/map/layers"
import ReportInfoTable from "./ReportInfoTable.vue"
export default {
  components: { ReportInfoTable },
  name: "EnvironmentAppliedReport",
  props: ["envNum", "responseData"],
  data() {
    return {
      colorValueRanges: [],
    }
  },

  computed: {
    descriptionTable() {
      if (this.responseData) {
        return this.responseData["environments"][this.envNum]["description_df"]
      }
      return null
    },
    agtNum() {
      if (this.responseData) {
        return this.responseData["environments"][this.envNum]["agt_id"]
      }
      return null
    },
    totalAcres() {
      if (this.responseData) {
        return this.responseData["environments"][this.envNum]["description_df"][
          "Total Acres"
        ]
      }
      return null
    },
    treatmentYield() {
      if (this.responseData) {
        return this.responseData["environments"][this.envNum]["description_df"][
          "Treatment Mean Yield"
        ]
      }
      return null
    },
    controlYield() {
      if (this.responseData) {
        return this.responseData["environments"][this.envNum]["description_df"][
          "Control Mean Yield"
        ]
      }
      return null
    },
    // big boxplot info
    chartOptions() {
      if (this.responseData) {
        let dataLabels = this.responseData["environments"][this.envNum][
          "boxplot"
        ]["dataLabels"]
        let formatter_data = this.responseData["environments"][this.envNum][
          "boxplot"
        ]["formatter_data"]
        dataLabels["formatter"] = function(
          value,
          { seriesIndex, dataPointIndex }
        ) {
          return formatter_data[seriesIndex][dataPointIndex]
        }
        return {
          chart: this.responseData["environments"][this.envNum]["boxplot"][
            "chart"
          ],
          legend: this.responseData["environments"][this.envNum]["boxplot"][
            "legend"
          ],
          title: this.responseData["environments"][this.envNum]["boxplot"][
            "title"
          ],
          plotOptions: this.responseData["environments"][this.envNum][
            "boxplot"
          ]["plotOptions"],
          yaxis: this.responseData["environments"][this.envNum]["boxplot"][
            "yaxis"
          ],
          xaxis: this.responseData["environments"][this.envNum]["boxplot"][
            "xaxis"
          ],
          colors: this.responseData["environments"][this.envNum]["boxplot"][
            "colors"
          ],
          dataLabels: dataLabels,
        }
      }
      return null
    },
    chartSeries() {
      if (this.responseData) {
        const output = this.responseData["environments"][this.envNum][
          "boxplot"
        ]["series"]
        return output
      }
      return null
    },
    // barchart-env info
    envOptions() {
      if (this.responseData) {
        return {
          chart: this.responseData["environments"][this.envNum]["barchart-env"][
            "chart"
          ],
          legend: this.responseData["environments"][this.envNum][
            "barchart-env"
          ]["legend"],
          title: this.responseData["environments"][this.envNum]["barchart-env"][
            "title"
          ],
          plotOptions: this.responseData["environments"][this.envNum][
            "barchart-env"
          ]["plotOptions"],
          xaxis: this.responseData["environments"][this.envNum]["barchart-env"][
            "xaxis"
          ],
          yaxis: this.responseData["environments"][this.envNum]["barchart-env"][
            "yaxis"
          ],
        }
      }
      return null
    },
    envSeries() {
      if (this.responseData) {
        const output = this.responseData["environments"][this.envNum][
          "barchart-env"
        ]["series"]
        return output
      }
      return null
    },
    // radar chart info
    radarOptions() {
      if (this.responseData) {
        return {
          chart: this.responseData["environments"][this.envNum]["radar"][
            "chart"
          ],
          xaxis: this.responseData["environments"][this.envNum]["radar"][
            "xaxis"
          ],
          yaxis: this.responseData["environments"][this.envNum]["radar"][
            "yaxis"
          ],
          title: this.responseData["environments"][this.envNum]["radar"][
            "title"
          ],
          subtitle: this.responseData["environments"][this.envNum]["radar"][
            "subtitle"
          ],
        }
      }
      return null
    },
    radarSeries() {
      if (this.responseData) {
        const output = this.responseData["environments"][this.envNum]["radar"][
          "series"
        ]
        return output
      }
      return null
    },
  },

  methods: {
    fitBoundsRotated(bounds, options, eventData, map) {
      options = Object.assign(
        {
          padding: {
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
          },
          offset: [0, 0],
          maxZoom: 14.8,
          //   maxZoom: map.transform.maxZoom,
        },
        options
      )

      if (typeof options.padding === "number") {
        const p = options.padding
        options.padding = {
          top: p,
          bottom: p,
          right: p,
          left: p,
        }
      }

      bounds = mapboxgl.LngLatBounds.convert(bounds)

      // paddingOffset affects the map's center, used in the options for
      // `easeTo` and `flyTo` in subsequent calls
      const paddingOffset = [
          options.padding.left - options.padding.right,
          options.padding.top - options.padding.bottom,
        ],
        lateralPadding = Math.min(options.padding.right, options.padding.left),
        verticalPadding = Math.min(options.padding.top, options.padding.bottom)

      options.offset = [
        options.offset[0] + paddingOffset[0],
        options.offset[1] + paddingOffset[1],
      ]
      options.bearing = options.bearing || this.getBearing()

      const offset = mapboxgl.Point.convert(options.offset),
        tr = map.transform,
        nw = tr.project(bounds.getNorthWest()),
        se = tr.project(bounds.getSouthEast()),
        size = se.sub(nw)

      // START CUSTOM ROTATION
      // "cropped rectangle rotation method"
      // https://stackoverflow.com/questions/33866535/how-to-scale-a-rotated-rectangle-to-always-fit-another-rectangle
      const theta = options.bearing * (Math.PI / 180),
        W =
          size.x * Math.abs(Math.cos(theta)) +
          size.y * Math.abs(Math.sin(theta)),
        H =
          size.x * Math.abs(Math.sin(theta)) +
          size.y * Math.abs(Math.cos(theta)),
        rotatedSize = { x: W, y: H },
        // END CUSTOM ROTATION
        scaleX =
          (tr.width - lateralPadding * 2 - Math.abs(offset.x) * 2) /
          rotatedSize.x,
        scaleY =
          (tr.height - verticalPadding * 2 - Math.abs(offset.y) * 2) /
          rotatedSize.y

      if (scaleY < 0 || scaleX < 0) {
        if (typeof console !== "undefined")
          console.warn(
            "Map cannot fit within canvas with the given bounds, padding, and/or offset."
          )
        return map
      }

      options.center = tr.unproject(nw.add(se).div(2))
      options.zoom = Math.min(
        tr.scaleZoom(tr.scale * Math.min(scaleX, scaleY)),
        options.maxZoom
      )

      return options.linear
        ? map.easeTo(options, eventData)
        : map.flyTo(options, eventData)
    },

    getColorForYield(properties, valueRanges) {
      const { cropId, yieldPerAcre } = properties
      const valueRange = valueRanges[cropId]

      if (yieldPerAcre <= 0 || yieldPerAcre === null) return [0, 0, 0, 0]

      const colorIdx =
        valueRange.length -
        valueRange.filter(cutoffVal => yieldPerAcre <= cutoffVal).length

      return HARVEST_YIELD_COLORS[colorIdx]
    },

    getElevation(properties) {
      const { elevation } = properties
      const elevationScale = 0

      return elevation / elevationScale
    },

    setColorValueRanges(ranges) {
      this.colorValueRanges = ranges
    },
  },

  mounted() {
    mapboxgl.accessToken = MAPBOX_TOKEN
    const map = new mapboxgl.Map({
      preserveDrawingBuffer: true,
      container: "mapbox-map",
      height: "100%",
      width: "100%",
      style: "mapbox://styles/mapbox/satellite-v9",
      center: [-91.6, 34.4],
      zoom: 9,
      pitch: 0,
    })

    // create a feature collection for visualizing yield by voxel
    const yieldFeatures = []
    for (const voxel in this.responseData["environments"][this.envNum]["map"][
      "voxel"
    ]) {
      const {
        seed_variety,
        topo_elevation,
        yield_vol_dry_bushels_per_ac,
        geom,
      } = this.responseData["environments"][this.envNum]["map"]["voxel"][voxel]

      yieldFeatures.push({
        type: "Feature",
        geometry: geom,
        properties: {
          seed_variety,
          elevation: topo_elevation,
          yieldPerAcre: yield_vol_dry_bushels_per_ac,
        },
      })
    }
    const voxelFeatureCollection = {
      type: "FeatureCollection",
      features: yieldFeatures,
    }

    // for voxel color by yield
    this.setColorValueRanges(this.responseData["yield_thresholds"])

    const yieldLayer = new MapboxLayer({
      id: "yieldLayer",
      type: GeoJsonLayer,
      data: voxelFeatureCollection,
      stroked: false,
      filled: true,
      extruded: true,
      autoHighlight: false,
      pointRadiusScale: 5,
      getElevation: feat => this.getElevation(feat.properties),
      getFillColor: feat =>
        this.getColorForYield(feat.properties, {
          undefined: this.colorValueRanges,
        }),
      getLineColor: () => [0, 0, 0, 255],
      lineWidthMinPixels: 0,
    })

    // calculate fieldBoundaries for zooming
    const calculateExtentBounds = fieldBoundData => {
      if (!fieldBoundData) return {}
      const latLongValues = fieldBoundData.features.map(
        feat => feat.geometry.coordinates
      )

      // aggregates latitude/longitude values in coordinates [[lat, lng]...]
      function getLong(n) {
        return n[0].map(subarr => subarr[0])
      }
      function getLat(n) {
        return n[0].map(subarr => subarr[1])
      }
      const longVals = _.flatMap(latLongValues, getLong)
      const latVals = _.flatMap(latLongValues, getLat)

      const minLatitude = Math.min.apply(null, latVals)
      const maxLatitude = Math.max.apply(null, latVals)
      const minLongitude = Math.min.apply(null, longVals)
      const maxLongitude = Math.max.apply(null, longVals)

      return { minLatitude, maxLatitude, minLongitude, maxLongitude }
    }
    let fieldExtentBounds = calculateExtentBounds(voxelFeatureCollection)
    this.fitBoundsRotated(
      [
        [fieldExtentBounds.minLongitude, fieldExtentBounds.minLatitude],
        [fieldExtentBounds.maxLongitude, fieldExtentBounds.maxLatitude],
      ],
      { bearing: -0.0001 },
      null,
      map
    )

    const fieldBoundLayer = new MapboxLayer({
      id: "fieldbound-layer",
      type: GeoJsonLayer,
      data: this.responseData["environments"][this.envNum]["map"][
        "field_boundary"
      ],
      stroked: true,
      filled: false,
      extruded: false,
      lineWidthMinPixels: 3,
      getLineColor: () => [255, 204, 0, 255],
    })

    const controlBoundLayer = new MapboxLayer({
      id: "control-layer",
      type: GeoJsonLayer,
      data: this.responseData["environments"][this.envNum]["map"][
        "control_boundary"
      ],
      stroked: true,
      filled: false,
      extruded: false,
      lineWidthMinPixels: 2,
      getLineColor: () => [154, 18, 179, 255],
    })

    const trialBoundLayer = new MapboxLayer({
      id: "trial-layer",
      type: GeoJsonLayer,
      data: this.responseData["environments"][this.envNum]["map"][
        "treatment_boundary"
      ],
      stroked: true,
      filled: false,
      extruded: false,
      lineWidthMinPixels: 2,
      getLineColor: () => [0, 128, 128, 255],
    })

    map.on("load", () => {
      map.addLayer(fieldBoundLayer)
      map.addLayer(controlBoundLayer)
      map.addLayer(trialBoundLayer)
      map.addLayer(yieldLayer)
    })
  },
}
</script>
<style>
@page {
  size: 8.5in 11in;
  margin: 0;
}

@media print {
  /* prevent blank page at end */
  table:last-of-type {
    page-break-after: auto;
  }
  html,
  body {
    height: 100%;
  }
}
</style>
<style scoped>
body {
  margin: 0;
  padding: 0;
  height: 100%;
}

.page:last-of-type {
  margin-bottom: -2px;
}

.pdf-wrapper {
  width: 1000px;
  height: 1283px;
  position: fixed;
}

.background-dim {
  background-color: #dcdcdc;
}

.map-footer {
  margin-top: 12px;
  padding-bottom: 12px;
}

.scale-text {
  color: white;
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  font-weight: bold;
}

.yield-scale {
  color: black;
  font-weight: bold;
  font-size: 16px;
  margin-left: 6px;
}

.control-wrapper {
  position: absolute;
  top: 6px;
  right: 26px;
  height: 90px;
  width: 100px;
  z-index: 500;
  background: white;
  border: 1pt solid #eee;
  box-shadow: 0px 0px 4px #ccc;
  padding: 8px;
  border-radius: 7px;
}

.map-wrapper {
  height: 350px;
  position: relative;
  background: #1b1b1d;
  overflow: hidden;
  padding-right: 25px;
}

.fill-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.title {
  color: black;
  font-family: Montserrat, sans-serif;
  font-size: 32px;
  font-weight: 500;
  margin-left: 25px;
}

.subtitle {
  color: black;
  font-family: Montserrat, sans-serif;
  font-size: 16px;
  font-weight: 500;
  margin-left: 25px;
}

.top-logo {
  position: absolute;
  top: 10px;
  right: 25px;
}

td {
  font-family: Montserrat, sans-serif;
  font-size: 18px;
}

td:first-of-type {
  text-align: right;
}

.dk-grey {
  color: #444;
}

.footer {
  position: absolute;
  bottom: 0px;
  height: 30px;
  width: 100%;
  background: #444;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #fff;
  font-weight: bold;
}

.green-cell {
  font-weight: bold;
  color: #79c61c;
}

.red-fill {
  background-color: #ff0000;
}

.orange-fill {
  background-color: #ff5c00;
}

.light-orange-fill {
  background-color: #ff8200;
}

.yellow-fill {
  background-color: #ffd500;
}

.bright-yellow-fill {
  background-color: #ffff00;
}

.light-green-fill {
  background-color: #c8e632;
}

.bright-green-fill {
  background-color: #b4fa00;
}

.green-fill {
  background-color: #40ff00;
}

.control-dot {
  height: 12px;
  width: 12px;
  background-color: #9a12b3;
  border-radius: 50%;
  display: inline-block;
  margin-top: 6px;
}

.trial-dot {
  height: 12px;
  width: 12px;
  background-color: #008080;
  border-radius: 50%;
  display: inline-block;
  margin-top: 6px;
}

.field-dot {
  height: 12px;
  width: 12px;
  background-color: #ffcc00;
  border-radius: 50%;
  display: inline-block;
  margin-top: 6px;
}

.dot-text {
  margin-left: 8px;
}

.lg-font {
  font-size: 16px;
}
</style>
